import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal } from 'antd';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import { selectedQuote } from './FinancingGridCard';
import SolarQuoteDetails, { SolarDetails } from './SolarQuoteDetails';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import { AnyMxRecord } from 'dns';
import RoofQuoteDetails from './RoofQuoteDetails';
import {
    buildQuoteFormTabs,
    columnIncludesBattery,
    columnIncludesRoof,
    columnIncludesSolar,
} from './quoteCreationProducts';
import { BatteryQuoteDetails } from './BatteryQuoteDetails';
import { effectiveQuoteGroupIdForSrPack } from '../../../../oneButton/quoteGroupContractRules';
import DashboardModal from './DashboardModal';
import './oneButton-css/GenerateQuoteModal.css';
import designServices from '../../../../services/oneButtonServices/designServices';

export interface QuoteFormState {
    pricingMethod: string;
    apr: string;
    escalator: string;
    term: string;
    grossPpw: string;
    downPayment: string;
    monthly: number;
}

export interface GenerateQuoteModalProps {
    confirmQuoteCellKey: selectedQuote | null;
    quoteForm: QuoteFormState | null;
    priceQuote: any;
    setConfirmQuoteCellKey: React.Dispatch<React.SetStateAction<selectedQuote | null>>;
    setQuoteForm: React.Dispatch<React.SetStateAction<QuoteFormState | null>>;
    updateQuoteVersion: () => void;
    noticeApi?: any;
    rcomendedQuote?: boolean;
}

export interface OpportunityInterestObject {
    Solar: boolean,
    Battery: boolean,
    BatteryOnly: boolean,
    Roof: boolean,
    RoofOnly: boolean,
    RRR: boolean,
    RR: boolean
}

export interface SolarObject {
    IsDomestic: boolean,
    SolarPackage: boolean,
    FinancePartnerId: number,
    Total_Amount: number,
    Deposit_Amount: any,
    ContractType: string,
    Tax: number,
    DownPayment: number,
    EPC: number,
    Escalator: string,
    PPW: number,
    Rate: any,
    Rebate: number,
    TermYears: number,
    PricingMethod: string,
    PricingValue: number,
    ITC_Amount: any,
    Monthly_Finance_Payment_NoITCPayback: any,
    Rebate_Method: string,
    Rebate_Amount_PerWatt: number,
    Adder_Price: number,
    Desired_PPW_Net: number,
    Finance_Partner_Product_Id: string,
    IsACH: boolean,
    Product_Type: string,
    Financed_Amount: any,
    AmountOnContractCompletion: number,
    AmountOnPhysicalInstallCompletion: number,
    AmountOnPhysicalInstallStart: number,
    AmountOnWorkCompletion: any,
    Finance_partner_contract_Type: string,
    Offer_Id: string
}

export interface nonSolarObject {
    SolarPackage: boolean,
}

type FooterMessage = {
    type: 'error' | 'success';
    text: string;
};

const LIGHTREACH_SYNC_REQUIRED_MESSAGE =
    'The Utility infomation or System design has changed since the last update to Lightreach, but these changes have not yet been synced. Please return to the Design page, click Save, and confirm that the changes are synced to Lightreach. This step ensures that your new quote reflects the correct system size and provides accurate financial data.';

type LightreachEligibleProductData = Record<string, any>;
type LightreachPriceCapsData = Record<string, any>;

interface SolarMasterSyncValues {
    enabled: boolean;
    termApr?: string;
    pricingMethod?: string;
    escalator?: string;
}

export interface priceQuote {
    SalesForce_Id: string,
    Opportunity_Id: number,
    apr: string[],
    RoofSquareOverride: AnyMxRecord,
    Cloned_Quote_Id: number,
    InstallationState: string,
    PriceTier: null,
    SfdcSyncValue: string,
    TotalSaleAmount: number,
    TotalCommissionableAmount: any,
    PurchaseMethod: any,
    RoofFinancingPartner: any,
    RoofFinanceMethod: any,
    RoofFinanceTerm: any,
    RoofCostType: any,
    RoofCommissionTier: any,
    LowTotal: number,
    RR_Price: any,
    Gross_Sales_Price_Per_Watt: any,
    Dealer_Fee_Percentage: number,
    Dealer_Epc: number,
    Monthly_Finance_Partner_Payment: any,
    Offer_Id: any,
    Production_rate: number,
    Low_Slope_Override: any,
    Normal_Slope_Override: any,
    LowSlope: any,
    NormalSlope: any,
    RR_System_Size: any,
    RoofProductId: any,
    RoofOnlyProductId: any,
    BatteryProductId: any,
    BatteryOnlyProductId: any,
    SolarProductId: any,
    RRProductId: any,


    OpportunityInterest: OpportunityInterestObject,

    Solar: SolarObject | nonSolarObject,

    Battery: any,

    BatteryOnlyPackage: any,

    RR: any,

    RoofQuote: any,

    RoofOnlyPackage: any,

    RoofOnly: any

    Roof: any

    RoofAdders: any[],
    BatteryAdders: any[]
}

/** Trinity `GenerateQuoteComponent.checkProductTypeFinancePartner` — maps FinancePartnerId on the save payload to `Finance_Partner` from opportunity products. */
function checkProductTypeFinancePartner(obj: any, productType: string, opportunityDetails: any): string {
    const pt = String(productType || '');
    let id: number | undefined;
    if (pt.includes('S')) id = obj?.Solar?.FinancePartnerId;
    else if (pt.includes('R')) {
        id = obj?.RoofOnlyPackage?.FinancePartnerId ?? obj?.RoofQuote?.FinancePartnerId;
    } else if (pt.includes('B')) {
        id = obj?.Battery?.FinancePartnerId ?? obj?.BatteryOnlyPackage?.FinancePartnerId;
    }

    const products =
        opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    if (!Array.isArray(products) || id == null || Number.isNaN(Number(id))) return '';

    const row = products.find((p: any) => {
        const pid = Number(p.Finance_Partner_Id ?? p.FinancePartnerId ?? p.finance_Partner_Id);
        return Number(id) === pid;
    });
    return String(row?.Finance_Partner ?? row?.finance_Partner ?? '').trim();
}


export const GenerateQuoteModal: React.FC<GenerateQuoteModalProps> = ({
    confirmQuoteCellKey,
    quoteForm,
    priceQuote,
    setConfirmQuoteCellKey,
    setQuoteForm,
    updateQuoteVersion,
    noticeApi,
    rcomendedQuote = false,
}) => {
    const { theme, selectedFinanceCells, financingGrid } = useOneButtonStore();
    const [loaderCount, setLoaderCount] = useState<number>(0);
    const [solarDetails, setSolarDetails] = React.useState<SolarDetails | null>(null);
    const [batteryDetails, setBatteryDetails] = React.useState<any>(null);
    const [eligibleProduct, setEligibleProduct] = React.useState<string[]>([]);
    const [sunnovaAPIDetails, setSunnovaAPIDetails] = React.useState<any>(null);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const roofDetails = useOneButtonStore((s) => s.roofDetails);
    const utilityDetailsDirty = useOneButtonStore((s) => s.utilityDetailsDirty);
    const [footerMessage, setFooterMessage] = useState<FooterMessage | null>(null);
    const [quoteGroupId, setQuoteGroupId] = useState<any>(null);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [lightreachEligibleProductData, setLightreachEligibleProductData] = useState<LightreachEligibleProductData[]>([]);
    const [lightreachPriceCapsData, setLightreachPriceCapsData] = useState<LightreachPriceCapsData[]>([]);
    /** Stacked above Generate Quote `DashboardModal` (10080); matches `ProductionsCard` force-sync confirm. */
    const [lightreachSyncRequiredOpen, setLightreachSyncRequiredOpen] = useState(false);

    const changeOrderDetails = useOneButtonStore((s) => s.changeOrderDetails);

    const systemDesign = useOneButtonStore((s) => s.systemDesign);

    const notifClass = `obd-notification obd-theme-${theme}`;



    const normalizeFinanceToken = (value: unknown): string =>
        String(value ?? '')
            .replace(/\s+/g, '')
            .trim()
            .toLowerCase();

    

    const solarMasterValues = useMemo(() => ({
        termApr: String(solarDetails?.TermAndAPR ?? '').trim(),
        pricingMethod: String(solarDetails?.pricingmethodName ?? '').trim(),
        escalator: String(solarDetails?.Escalator ?? '').trim(),
        partner: normalizeFinanceToken(solarDetails?.financePartnerName),
        method: normalizeFinanceToken(solarDetails?.contract_Type),
    }), [solarDetails]);

    const batterySolarMasterSync = useMemo<SolarMasterSyncValues>(() => {
        const batteryPartner = normalizeFinanceToken(batteryDetails?.financePartnerName);
        const batteryMethod = normalizeFinanceToken(batteryDetails?.financeMethodName);
        const enabled =
            !!solarMasterValues.partner &&
            !!solarMasterValues.method &&
            solarMasterValues.partner === batteryPartner &&
            solarMasterValues.method === batteryMethod;
        return {
            enabled,
            termApr: solarMasterValues.termApr,
            pricingMethod: solarMasterValues.pricingMethod,
            escalator: solarMasterValues.escalator,
        };
    }, [batteryDetails, solarMasterValues]);

    const roofSolarMasterSync = useMemo<SolarMasterSyncValues>(() => {
        const roofPartner = normalizeFinanceToken(roofDetails?.financePartnerName);
        const roofMethod = normalizeFinanceToken(roofDetails?.contract_Type);
        const enabled =
            !!solarMasterValues.partner &&
            !!solarMasterValues.method &&
            solarMasterValues.partner === roofPartner &&
            solarMasterValues.method === roofMethod;

        return {
            enabled,
            termApr: solarMasterValues.termApr,
        };
    }, [roofDetails, solarMasterValues]);


    const getQuoteGroupIdForGeneration = async (): Promise<any | null> => {
        updateLoaderCount(true);
        try {
            return (await quoteServices.getQuoteGroupID(opportunityDetails.Opportunity_Id)) || 0;
        } catch (error) {
            console.error('Failed to fetch quote group id:', error);
            setFooterMessage({ type: 'error', text: 'Unable to initialize quote generation. Please try again.' });
            return null;
        } finally {
            updateLoaderCount(false);
            setIsGenerating(false);
        }
    };

    const handleOk = async () => {
        setIsGenerating(true);
        const quoteProduct = confirmQuoteCellKey?.product;
        if (
            utilityDetailsDirty &&
            quoteProduct &&
            columnIncludesSolar(quoteProduct)
        ) {
            const msg =
                'Utility details have unsaved changes. Open Utility details, click Save, then create the quote.';
            setFooterMessage({ type: 'error', text: msg });
            setIsGenerating(false);
            return;
        }
        
        let lightreachProducts = opportunityDetails.OpportunityProduct.filter((x: any) => x.Finance_Partner_Display_Name.toLocaleLowerCase() === 'lightreach');
        if(lightreachProducts && lightreachProducts.length > 0 && changeOrderDetails && changeOrderDetails.length > 0){

            const lightReachData = changeOrderDetails.find((x: any) => x.Finance_Partner_Name.toLocaleLowerCase() == COMMON_CONSTANTS.LightreachPartnerName.toLocaleLowerCase());
            
            if (lightReachData) {
                if (lightReachData.Is_Change_Order && (lightReachData?.Is_Utility_Updated || lightReachData.Is_System_Design_Updated)){
                    setLightreachSyncRequiredOpen(true);
                    setIsGenerating(false);
                    return;
                } 
                // else {
                //     const oppId = opportunityDetails?.Opportunity_Id;
                //     if (oppId != null && String(oppId).trim() !== '') {
                //         updateLoaderCount(true);
                //         try {
                //             const designCheckMessage = await designServices.checkNewDesignExist(oppId);
                //             if (designCheckMessage?.data?.Success) {
                //                 setLightreachSyncRequiredOpen(true);
                //                 setIsGenerating(false);
                //                 return;
                //             }
                //         } catch {
                //             setFooterMessage({
                //                 type: 'error',
                //                 text: 'Unable to verify system design before quote. Please try again.',
                //             });
                //             setIsGenerating(false);
                //             return;
                //         } finally {
                //             updateLoaderCount(false);
                //         }
                //     }
                // }
            } 
        }
        


        if (confirmQuoteCellKey?.product === 'Solar' && solarDetails?.valid) {
            const solarProduct = opportunityDetails.OpportunityProduct.find((product: any) => product.Product_Type_Name === 'Solar');
            const solarMethodMatch =
                String(solarProduct?.Finance_Method_Name ?? '').trim() === String(solarDetails.contract_Type ?? '').trim();
            if (solarProduct && solarProduct.ActiveFlag && solarProduct.Finance_Partner
                && solarProduct.Finance_Partner_Display_Name.replace(' ', '').toLocaleLowerCase() === solarDetails.financePartnerName.replace(' ', '').toLocaleLowerCase()
                && (solarMethodMatch || solarProduct.Finance_Partner_Display_Name.trim() === 'Cash' 
                || solarProduct.Finance_Partner_Display_Name.trim() === 'GoodLeap' || solarProduct.Finance_Partner_Display_Name.trim() === 'IGS')) {
                solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
                
            } else {
                setFooterMessage({ type: 'error', text: 'Finance partner or finance method is not active for this product' });
                setIsGenerating(false);
            }


        } else if (confirmQuoteCellKey?.product === 'Roof' && roofDetails?.valid && !!roofDetails?.gross_Sales_Price && roofDetails?.gross_Sales_Price != "0" && roofDetails.Roofsq != 0) {
            const roofProduct = opportunityDetails.OpportunityProduct.find((product: any) => product.Product_Type_Name === 'Roof');
            const roofMethodMatch =
                String(roofProduct?.Finance_Method_Name ?? '').trim() === String(roofDetails.contract_Type ?? '').trim();
            if (roofProduct && roofProduct.ActiveFlag && roofProduct.Finance_Partner
                && roofProduct.Finance_Partner_Display_Name.replace(' ', '').toLocaleLowerCase() === roofDetails.financePartnerName.replace(' ', '').toLocaleLowerCase()
                && (roofMethodMatch || roofProduct.Finance_Partner_Display_Name.trim() === 'Cash')) {
                roofDetails.financePartnerId = roofProduct.Finance_Partner_Id;
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
            } else {
                setFooterMessage({ type: 'error', text: 'Finance partner or finance method is not active for this product' });
                setIsGenerating(false);
            }
        } else if (confirmQuoteCellKey?.product === 'Battery' && batteryDetails?.valid) {
            const batteryProduct = opportunityDetails.OpportunityProduct.find((product: any) => product.Product_Type_Name === 'Battery');
            const batteryMethodMatch =
                String(batteryProduct?.Finance_Method_Name ?? '').trim() === String(batteryDetails.financeMethodName ?? '').trim();
            if (batteryProduct && batteryProduct.ActiveFlag && batteryProduct.Finance_Partner
                && batteryProduct.Finance_Partner_Display_Name.replace(' ', '').toLocaleLowerCase() === batteryDetails.financePartnerName?.replace(' ', '').toLocaleLowerCase()
                && (batteryMethodMatch || batteryProduct.Finance_Partner_Display_Name.trim() === 'Cash')) {
                batteryDetails.financePartnerId = batteryProduct.Finance_Partner_Id;
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
            } else {
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
            }
        } else if (confirmQuoteCellKey?.product === 'Solar + Battery' && solarDetails?.valid && batteryDetails?.valid) {
            const solarProduct = opportunityDetails.OpportunityProduct.find((product: any) => product.Product_Type_Name === 'Solar');
            const solarMethodMatch =
                String(solarProduct?.Finance_Method_Name ?? '').trim() === String(solarDetails.contract_Type ?? '').trim();

            const batteryProduct = opportunityDetails.OpportunityProduct.find((product: any) => product.Product_Type_Name === 'Battery');
            const batteryMethodMatch =
                String(batteryProduct?.Finance_Method_Name ?? '').trim() === String(batteryDetails.financeMethodName ?? '').trim();

            if (solarProduct && solarProduct.ActiveFlag && solarProduct.Finance_Partner
                && solarProduct.Finance_Partner_Display_Name.replace(' ', '').toLocaleLowerCase() === solarDetails.financePartnerName.replace(' ', '').toLocaleLowerCase()
                && (solarMethodMatch || solarProduct.Finance_Partner_Display_Name.trim() === 'Cash')
                && batteryProduct && batteryProduct.ActiveFlag && batteryProduct.Finance_Partner
                && batteryProduct.Finance_Partner_Display_Name.replace(' ', '').toLocaleLowerCase() === batteryDetails.financePartnerName.replace(' ', '').toLocaleLowerCase()
                && (batteryMethodMatch || batteryProduct.Finance_Partner_Display_Name.trim() === 'Cash')) {
                solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
            } else {
                setFooterMessage({ type: 'error', text: 'Finance partner or finance method is not active for this product' });
                setIsGenerating(false);
            }
        } else if (confirmQuoteCellKey?.product.trim() === 'Solar + Roof' && solarDetails?.valid && roofDetails?.valid && !!roofDetails?.gross_Sales_Price && roofDetails?.gross_Sales_Price != "0" && roofDetails.Roofsq != 0) {
            const solarProduct = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Solar');
            const roofProduct = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Roof');
            if (solarProduct?.ActiveFlag && roofProduct?.ActiveFlag) {
                solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
            } else {
                setFooterMessage({ type: 'error', text: 'Finance partner or finance method is not active for this product' });
                setIsGenerating(false);
            }
        } else if (confirmQuoteCellKey?.product.trim() === 'Battery + Roof' && batteryDetails?.valid && roofDetails?.valid && !!roofDetails?.gross_Sales_Price && roofDetails?.gross_Sales_Price != "0" && roofDetails.Roofsq != 0) {
            const batteryProduct = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Battery');
            const roofProduct = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Roof');
            const batteryMethodMatch =
                String(batteryProduct?.Finance_Method_Name ?? '').trim() === String(batteryDetails.financeMethodName ?? '').trim();
            const roofMethodMatch =
                String(roofProduct?.Finance_Method_Name ?? '').trim() === String(roofDetails.contract_Type ?? '').trim();
            if (batteryProduct && batteryProduct.ActiveFlag && batteryProduct.Finance_Partner
                && batteryProduct.Finance_Partner_Display_Name.replace(' ', '').toLocaleLowerCase() === batteryDetails.financePartnerName?.replace(' ', '').toLocaleLowerCase()
                && (batteryMethodMatch || batteryProduct.Finance_Partner_Display_Name.trim() === 'Cash')
                && roofProduct && roofProduct.ActiveFlag && roofProduct.Finance_Partner
                && roofProduct.Finance_Partner_Display_Name.replace(' ', '').toLocaleLowerCase() === roofDetails.financePartnerName.replace(' ', '').toLocaleLowerCase()
                && (roofMethodMatch || roofProduct.Finance_Partner_Display_Name.trim() === 'Cash')) {
                batteryDetails.financePartnerId = batteryProduct.Finance_Partner_Id;
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
            } else {
                setFooterMessage({ type: 'error', text: 'Finance partner or finance method is not active for this product' });
                setIsGenerating(false);
            }
        } else if(confirmQuoteCellKey?.product.trim() === 'Solar + Battery + Roof' && solarDetails?.valid && batteryDetails?.valid && roofDetails?.valid && !!roofDetails?.gross_Sales_Price && roofDetails?.gross_Sales_Price != "0" && roofDetails.Roofsq != 0) {
            const solarProduct = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Solar');
            const batteryProduct = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Battery');
            const roofProduct = opportunityDetails.OpportunityProduct.find((p: any) => p.Product_Type_Name === 'Roof');

            if (solarProduct?.ActiveFlag && roofProduct?.ActiveFlag && batteryProduct?.ActiveFlag) {
                solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                const groupId = await getQuoteGroupIdForGeneration();
                if (groupId == null) return;
                setQuoteGroupId(groupId);
                GenerateQuote(groupId);
            } else {
                setFooterMessage({ type: 'error', text: 'Finance partner or finance method is not active for this product' });
                setIsGenerating(false);
            }
        } else {
            if(batteryDetails?.valid) {
                setFooterMessage({ type: 'error', text: 'Finance partner or finance method is not active for this product' });
            }
            setIsGenerating(false);
        }
    };



    const tabs = useMemo(() => {
        if (!confirmQuoteCellKey) return [];
        return buildQuoteFormTabs(confirmQuoteCellKey, selectedFinanceCells, financingGrid);
    }, [confirmQuoteCellKey, selectedFinanceCells, financingGrid]);
    let isLightReach = false;
    if(confirmQuoteCellKey?.partner) {
        isLightReach = confirmQuoteCellKey?.partner?.toLocaleLowerCase() === 'lightreach';
    } else if(confirmQuoteCellKey?.products && confirmQuoteCellKey?.products?.length > 0) {
        isLightReach = confirmQuoteCellKey?.products?.some((product: any) => product?.partner?.toLocaleLowerCase() === 'lightreach');
    }

    const getLightreachData = async () => {
        let disposed = false;
        const hasSolarTab = tabs.some((tab) => tab.kind === 'solar');
        const syncLightreachData = async () => {
            if (!isLightReach || !hasSolarTab) {
                setLightreachEligibleProductData([]);
                setLightreachPriceCapsData([]);
                return;
            }
            const opportunityId = priceQuote?.Opportunity_Id;
            const leadId =
                opportunityDetails?.Finance_Partner_Sync_LeadID
                ?? priceQuote?.Finance_Partner_Sync_LeadID;

            if (!opportunityId || !leadId) {
                setLightreachEligibleProductData([]);
                setLightreachPriceCapsData([]);
                return;
            }

            updateLoaderCount(true);
            try {
                const [eligibleData, priceCaps] = await Promise.all([
                    quoteServices.getLightreachPricingByAccount(opportunityId, leadId),
                    quoteServices.getPriceCaps(leadId),
                ]);
                if (!disposed) {
                    setLightreachEligibleProductData(Array.isArray(eligibleData) ? eligibleData : []);
                    setLightreachPriceCapsData(Array.isArray(priceCaps) ? priceCaps : []);
                }
            } catch (error) {
                if (!disposed) {
                    setLightreachEligibleProductData([]);
                    setLightreachPriceCapsData([]);
                }
            } finally {
                if (!disposed) updateLoaderCount(false);
            }
        };
        syncLightreachData();

        return () => {
            disposed = true;
        };
    }

    const confirmQuoteCellIdentity = useMemo(
        () =>
            confirmQuoteCellKey
                ? `${confirmQuoteCellKey.product}::${confirmQuoteCellKey.partner ?? ''}`
                : '',
        [confirmQuoteCellKey?.product, confirmQuoteCellKey?.partner],
    );

    useEffect(() => {
        setFooterMessage(null);
        setLoaderCount(0);
        setIsGenerating(false);
        setLightreachSyncRequiredOpen(false);
        if (confirmQuoteCellIdentity) {
            getLightreachData();
        }
    }, [confirmQuoteCellIdentity]);

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            console.debug('[GenerateQuoteModal] loaderCount:', loaderCount);
        }
    }, [loaderCount]);

    const summaryParts = useMemo(() => {
        const parts: string[] = [];
        for (const k of selectedFinanceCells) {
            const idx = k.indexOf('::');
            if (idx < 0) continue;
            parts.push(`${k.slice(0, idx)} / ${k.slice(idx + 2)}`);
        }
        return parts;
    }, [selectedFinanceCells]);


    const handleCancel = () => {
        setLightreachSyncRequiredOpen(false);
        setLoaderCount(0);
        setIsGenerating(false);
        setFooterMessage(null);
        setConfirmQuoteCellKey(null);
        setQuoteForm(null);
        if(!rcomendedQuote) updateQuoteVersion();

    };


    // Step 1
    const GenerateQuote = async (quoteGroupId: any) => {
        if (confirmQuoteCellKey?.product === 'Solar' && (!priceQuote.EPCCalc.SystemSizeValue || priceQuote.EPCCalc.SystemSizeValue === null
            || parseInt(priceQuote.EPCCalc.SystemSizeValue)) == 0) {
            setIsGenerating(false);
            return;
        }



        // OBA-4736 Show the confirmation popup when light reach product didn't sync the utility and system design datas
        let message;
        // const changeOrderDetails = await this.masterService.updateChangeOrderDetailsObservable.pipe(take(1)).toPromise();
        // if (Array.isArray(changeOrderDetails)) {
        //     const lightReachData = changeOrderDetails.find((x: any) => x.Finance_Partner_Name == OpportunityConstant.CommonConstants.LightreachPartnerName);
        //     if (lightReachData) {
        //         if (lightReachData.Is_Change_Order && (lightReachData.Is_Utility_Updated | lightReachData.Is_System_Design_Updated))
        //         message = this.setConfirmationMessage()
        //         else message = await this.CheckNewDesignExistAPI();
        //     }
        // }

        if (confirmQuoteCellKey?.product === 'Solar' && (!solarDetails?.financePartnerName || !solarDetails?.contract_Type)) {
            setIsGenerating(false);
            updateLoaderCount(false);
            setFooterMessage({ type: 'error', text: "Solar is missing the finance partner or contract type" });
        }
        else if (message) {
            setIsGenerating(false);
            updateLoaderCount(false);
            setFooterMessage({ type: 'error', text: String(message) });
        } else {
            // GoodLeap and LightReach (solar or roof) require finalized oneDRAW system losses before quote generation.
            const normFinanceName = (s: string | undefined) => (s || '').replace(/\s+/g, '').toLowerCase();
            const solarGoodLeap =
                normFinanceName(solarDetails?.financePartnerName) === 'goodleap' &&
                !String(solarDetails?.contract_Type || '').toLowerCase().includes('sunnova');
            const solarLightReach = normFinanceName(solarDetails?.financePartnerName) === 'lightreach';
            const roofGoodLeap = normFinanceName(roofDetails?.financePartnerName) === 'goodleap';
            const roofLightReach = normFinanceName(roofDetails?.financePartnerName) === 'lightreach';
            const solarNeedsDesignLosses = solarGoodLeap || solarLightReach;
            const roofNeedsDesignLosses = roofGoodLeap || roofLightReach;
            if (solarNeedsDesignLosses || roofNeedsDesignLosses) {
                const designReadiness = await GetOneDrawDesignReadiness();
                const product = confirmQuoteCellKey?.product;
                const needsLossesForProduct = (product?.includes('Solar') && solarGoodLeap);
                if (needsLossesForProduct && !designReadiness.hasSystemLosses) {
                    const message = designReadiness.opportunityStage === "4: Contracted"
                        ? "Design losses are missing in oneBUTTON. Please review the “Design” page and confirm that a oneDRAW design is available. If a oneDRAW design exists, finalize the design in oneDRAW, then refresh the Design page in oneBUTTON. Once refreshed, you should see the “System Losses” button, and will be able to submit the quote to your finance partner. If you are unable to finalize the design, or if no oneDRAW design is available, please contact RSA via Chatter on the Opportunity in Salesforce. Once RSA creates a new oneDRAW design or finalizes the existing design, you will be able to proceed."
                        : "Design losses are missing in oneBUTTON. Please re-finalize the design in oneDRAW, then refresh and save the Design page. Once completed, you can proceed with Quote Generation and continue processing the Change Order.";
                    setFooterMessage({ type: 'error', text: message });
                    setIsGenerating(false);
                    updateLoaderCount(false);
                    return;
                }
            }

            //Generate New quote-groupd Id

            const tasks: Promise<any>[] = [];
            switch (confirmQuoteCellKey?.product) {
                case 'Solar':
                    tasks.push((async () => {
                        const financialData = await getESSunnovaFinancialData(
                            "S",
                            quoteGroupId,
                            solarDetails?.financePartnerName,
                            (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                            (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                            (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                            (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                            (eligibleProduct?.length > 0) ? eligibleProduct : ''
                        );
                        return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                    })());
                    break;
                case 'Battery':
                    tasks.push((async () => {
                        const financialData = await getESSunnovaFinancialData(
                            'B',
                            quoteGroupId,
                            batteryDetails?.financePartnerName,
                            (batteryDetails?.financeMethodName) ? batteryDetails?.financeMethodName.trim() : '',
                            (batteryDetails?.pricingMethod) ? batteryDetails?.pricingMethod.trim() : '',
                            '', // Escalator
                            (batteryDetails?.termAndApr) ? batteryDetails?.termAndApr.trim() : '',
                            '' // Eligible Product
                        );
                        return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                    })());
                    break;
                case 'Roof':
                    tasks.push((async () => {
                        const financialData = await getESSunnovaFinancialData(
                            'R',
                            quoteGroupId,
                            roofDetails?.financePartnerName,
                            (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                            '', // Pricing Method
                            0,  // Escalator
                            (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                            '' // Eligible Product
                        );
                        return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                    })());
                    break;
                case 'Solar + Battery':
                    if (solarDetails?.contract_Type === batteryDetails?.financeMethodName 
                        && solarDetails?.financePartnerName === batteryDetails?.financePartnerName 
                        && solarDetails?.financePartnerName !== "Cash") {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SB',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SB", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                batteryDetails?.financePartnerName,
                                (batteryDetails?.financeMethodName) ? batteryDetails?.financeMethodName.trim() : '',
                                (batteryDetails?.pricingMethod) ? batteryDetails?.pricingMethod.trim() : '',
                                '', // Escalator
                                (batteryDetails?.termAndApr) ? batteryDetails?.termAndApr.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                "S",
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                        })());
                    }
                    break;
                case 'Solar + Roof':
                    if (String(solarDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(solarDetails?.contract_Type || '').trim() !== String(roofDetails?.contract_Type || '').trim()) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'S',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '', // Pricing Method
                                0,  // Escalator
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SR',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SR", quoteGroupId, financialData);
                        })());
                    }
                    break;
                case 'Battery + Roof':
                    // Same pattern as Solar + Battery / Solar + Roof: one combined BR call when finance lines align; otherwise two saves (B then R).
                    if (String(batteryDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(roofDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(batteryDetails?.financeMethodName || '').trim() !== String(roofDetails?.contract_Type || '').trim()) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                batteryDetails?.financePartnerName,
                                (batteryDetails?.financeMethodName) ? batteryDetails?.financeMethodName.trim() : '',
                                (batteryDetails?.pricingMethod) ? batteryDetails?.pricingMethod.trim() : '',
                                '',
                                (batteryDetails?.termAndApr) ? batteryDetails?.termAndApr.trim() : '',
                                ''
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '',
                                0,
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                ''
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'BR',
                                quoteGroupId,
                                batteryDetails?.financePartnerName,
                                (batteryDetails?.financeMethodName) ? batteryDetails?.financeMethodName.trim() : '',
                                (batteryDetails?.pricingMethod) ? batteryDetails?.pricingMethod.trim() : '',
                                '',
                                (batteryDetails?.termAndApr) ? batteryDetails?.termAndApr.trim() : '',
                                ''
                            );
                            return savePriceAndQuoteAPI("BR", quoteGroupId, financialData);
                        })());
                    }
                    break;
                case 'Solar + Battery + Roof':
                    if (solarDetails?.contract_Type === batteryDetails?.financeMethodName 
                        && solarDetails?.financePartnerName === batteryDetails?.financePartnerName 
                        && solarDetails?.financePartnerName !== "Cash") {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SB',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SB", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '', // Pricing Method
                                0,  // Escalator
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                    } else if (!(String(solarDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(solarDetails?.contract_Type || '').trim() !== String(roofDetails?.contract_Type || '').trim())) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SR',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SR", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                batteryDetails?.financePartnerName,
                                (batteryDetails?.financeMethodName) ? batteryDetails?.financeMethodName.trim() : '',
                                (batteryDetails?.pricingMethod) ? batteryDetails?.pricingMethod.trim() : '',
                                '', // Escalator
                                (batteryDetails?.termAndApr) ? batteryDetails?.termAndApr.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'S',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '', // Pricing Method
                                0,  // Escalator
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                batteryDetails?.financePartnerName,
                                (batteryDetails?.financeMethodName) ? batteryDetails?.financeMethodName.trim() : '',
                                (batteryDetails?.pricingMethod) ? batteryDetails?.pricingMethod.trim() : '',
                                '', // Escalator
                                (batteryDetails?.termAndApr) ? batteryDetails?.termAndApr.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                    }
                    break;
                default:
                    if (confirmQuoteCellKey?.product === 'Roof') updateLoaderCount(false);
            }

            if (tasks.length > 0) {
                setIsGenerating(true);
                try {
                    const results = await Promise.allSettled(tasks);
                    const hadSuccess = results.some((r) => r.status === 'fulfilled');
                    if (hadSuccess) {
                        postSavePriceAndQuoteOperations();
                    } 
                } catch (error) {
                    console.error("Error generating quotes:", error);
                } finally {
                    setIsGenerating(false);
                }
            }
        }
    }

    // Step 2
    function getESSunnovaFinancialData(productType: any, quoteGroupId: any, financePartnerselected: string | undefined,
        contract_Type: any, pricingMethod: any, escalator: any, termApr: any, eligibleProducts: any): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            let PriceQuotes = {
                "ExcelFileName": "Sunnova Financial Data",
                "SheetName": "SVA Financing Options",
                "InputValues": [{
                    "Type": "String",
                    "CellNumber": "B4",
                    "CellValue": priceQuote.InstallationState
                },
                {
                    "Type": "String",
                    "CellNumber": "C4",
                    "CellValue": productType == 'RR' ? 'R&R' : productType
                },
                {
                    "Type": "String",
                    "CellNumber": "D4",
                    "CellValue": financePartnerselected
                },
                {
                    "Type": "String",
                    "CellNumber": "F4",
                    "CellValue": contract_Type
                },
                {
                    "Type": "String",
                    "CellNumber": "K4",
                    "CellValue": pricingMethod
                },
                {
                    "Type": "String",
                    "CellNumber": "M4",
                    "CellValue": escalator
                },
                {
                    "Type": "String",
                    "CellNumber": "E8",
                    "CellValue": priceQuote.UtilityCompanyName || ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C8",
                    "CellValue": (eligibleProducts?.length > 0) ? eligibleProducts : ''
                }
                ],
                "OutputCells": ["T4", "U4", "V4", "W4", "X4", "Y4", "Z4", "AA4", 'AB4']
            };

            if (termApr.includes('ACH')) {
                if (termApr.includes('w/ ACH')) {
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "O4",
                        "CellValue": termApr.replace('w/ ACH', '').trim()
                    })
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "L11",
                        "CellValue": "Yes"
                    })
                } else {
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "O4",
                        "CellValue": termApr.replace('w/o ACH', '').trim()
                    })
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "L11",
                        "CellValue": "No"
                    })
                }
            } else {
                PriceQuotes.InputValues.push({
                    "Type": "String",
                    "CellNumber": "O4",
                    "CellValue": termApr
                })
                PriceQuotes.InputValues.push({
                    "Type": "String",
                    "CellNumber": "L11",
                    "CellValue": "Yes"
                })
            }
            PriceQuotes.InputValues.push({
                "Type": "String",
                "CellNumber": "E8",
                "CellValue": priceQuote.ExcelUtilityCompanyName || ''
            })
            updateLoaderCount(true);
            masterServices.cascadingDropDown(PriceQuotes).then((data: any) => {
                let sunnovaAPIDetailsTemp: any = {};
                if (data[0] != null) sunnovaAPIDetailsTemp.ID = data[0].CellValue;
                if (data[1] != null) sunnovaAPIDetailsTemp.Name = data[1].CellValue;
                if (data[2] != null) sunnovaAPIDetailsTemp.ContractID = data[2].CellValue;
                if (data[3] != null) sunnovaAPIDetailsTemp.TermLength = (financePartnerselected === 'Sunlight') ? (Number(data[3].CellValue) / 12) : data[3].CellValue;
                if (data[4] != null) sunnovaAPIDetailsTemp.APR = data[4].CellValue;
                if (data[5] != null) sunnovaAPIDetailsTemp.DealerFee = isNaN(Number(data[5].CellValue)) ? 0 : Number(data[5].CellValue); //Handled No Match Found case
                if (data[6] != null) sunnovaAPIDetailsTemp.PricingMethod = data[6].CellValue;
                if (data[7] != null) sunnovaAPIDetailsTemp.PaymentEscalator = data[7].CellValue;
                if (data[8] != null) sunnovaAPIDetailsTemp.SfdcSyncValue = data[8].CellValue;
                resolve(sunnovaAPIDetailsTemp);
                updateLoaderCount(false);
            }).catch((err: any) => {
                reject(err);
                updateLoaderCount(false);
            });
        });
    }

    // Step 3
    const savePriceAndQuoteAPI = (productType: any, quoteGroupId: any, sunnovaAPIDetails: any): Promise<any> => {
        return new Promise((resolve, reject) => {
            //For a new quote with Same finance Method
            let quoteIsMultiContract = false;
            // if (this.battery && this.battery.BatteryAdder) {
            //     if (this.battery.BatteryAdder.length == 2) {
            //         this.battery.BatteryAdder[1].amount = 0;
            //     }
            // }
            /**Solar details */

            let OpportunityInterest = { Solar: false, Battery: false, BatteryOnly: false, Roof: false, RoofOnly: false, RRR: false, RR: false }

            switch (productType) {
                case 'B':
                    OpportunityInterest.BatteryOnly = true;
                    break;
                case 'R':
                    if (priceQuote.OpportunityInterest?.RRR) OpportunityInterest.RRR = true;
                    else OpportunityInterest.RoofOnly = true;
                    break;
                case 'S':
                    OpportunityInterest.Solar = true;
                    break;
                case 'SB':
                    OpportunityInterest.Solar = true;
                    OpportunityInterest.Battery = true;
                    break
                case 'SR':
                    OpportunityInterest.Solar = true;
                    OpportunityInterest.Roof = true;
                    break;
                case 'SBR':
                    OpportunityInterest.Solar = true;
                    OpportunityInterest.Battery = true;
                    OpportunityInterest.Roof = true;
                    break;
                case 'BR':
                    OpportunityInterest.Battery = true;
                    OpportunityInterest.Roof = true;
                    break;
                default:

            }

            let payload: priceQuote = {
                SalesForce_Id: priceQuote.SalesForce_Id,
                Opportunity_Id: priceQuote.Opportunity_Id,
                apr: [sunnovaAPIDetails?.APR || (roofDetails?.apr ? roofDetails.apr + '%' : '')],
                RoofSquareOverride: priceQuote.RoofSquareOverride,
                Cloned_Quote_Id: priceQuote.Cloned_Quote_Id,
                InstallationState: priceQuote.InstallationState,
                PriceTier: priceQuote.PriceTier,
                SfdcSyncValue: sunnovaAPIDetails?.SfdcSyncValue || '',
                TotalSaleAmount: (productType === 'R' || productType === 'RRR') ? (priceQuote.TotalSaleAmount) : (solarDetails?.TotalSaleAmount ?? 0),
                TotalCommissionableAmount: null,
                PurchaseMethod: priceQuote.PurchaseMethod,
                RoofFinancingPartner: priceQuote.RoofFinancingPartner,
                RoofFinanceMethod: priceQuote.RoofFinanceMethod,
                RoofFinanceTerm: priceQuote.RoofFinanceTerm,
                RoofCostType: priceQuote.RoofCostType,
                RoofCommissionTier: priceQuote.RoofCommissionTier,
                LowTotal: priceQuote.LowTotal,
                RR_Price: priceQuote.RR_Price,
                Gross_Sales_Price_Per_Watt: productType === 'R' ? null : solarDetails?.grossSalesPricePerWatt ?? null,
                Dealer_Fee_Percentage: (productType === 'R' || productType === 'RRR') ? (priceQuote.Dealer_Fee_Percentage || 0) : (solarDetails?.dealerFeePercentage ?? 0),
                Dealer_Epc: (productType === 'R' || productType === 'RRR' || productType === 'B') ? 
                    (priceQuote.Dealer_Epc || 0) : (solarDetails?.dealerEpc ?? 0),
                Monthly_Finance_Partner_Payment: (productType === 'R' || productType === 'RRR') ? (priceQuote.Monthly_Finance_Partner_Payment || 0) : (solarDetails?.Monthly_Finance_Partner_Payment ?? 0),
                Offer_Id: priceQuote.Offer_Id,
                Production_rate: solarDetails?.productionRate ? Number(solarDetails.productionRate) : 0,
                Low_Slope_Override: priceQuote?.Low_Slope_Override || 0,
                Normal_Slope_Override: priceQuote?.Normal_Slope_Override || 0,
                LowSlope: priceQuote?.LowSlope || 0,
                NormalSlope: priceQuote?.NormalSlope || 0,
                RR_System_Size: opportunityDetails?.RR_System_Size ?? null,
                RoofProductId: priceQuote?.RoofProductId || 0,
                RoofOnlyProductId: priceQuote?.RoofProductId || 0,
                BatteryProductId: priceQuote?.BatteryProductId || 0,
                BatteryOnlyProductId: priceQuote?.BatteryOnlyProductId || 0,
                SolarProductId: priceQuote?.SolarProductId || 0,
                RRProductId: priceQuote?.RRProductId || 0,
                Solar: {
                    SolarPackage: false
                },
                Battery: {
                    BatteryPackage: false
                },
                BatteryOnlyPackage: {
                    BatteryOnlyPackage: false
                },
                RR: {
                    RRPackage: false,
                    DesignId: 0
                },
                RoofQuote: {
                    RoofPackage: false
                },
                RoofOnlyPackage: {
                    RoofOnlyPackage: false
                },
                RoofOnly: {
                    RoofSquare: 0,
                    RoofProduct: ""
                },
                Roof: {
                    RoofProductId: priceQuote?.Roof?.RoofProductId || 0,
                    RoofSquare: priceQuote?.Roof?.RoofSquare || 0,
                    ShingleColor: priceQuote?.Roof?.ShingleColor || null,
                    ShingleColorSF: priceQuote?.Roof?.ShingleColorSF || null,
                    RoofProduct: priceQuote?.Roof?.RoofProduct || "",
                    Rebate: priceQuote?.Roof?.Rebate || null,
                    TermYears: priceQuote?.Roof?.TermYears || null,
                    TermAPR: priceQuote?.Roof?.TermAPR || null,
                    EPC: priceQuote?.Roof?.EPC || null,
                    PricingMethod: priceQuote?.Roof?.PricingMethod || null,
                    PricingValue: priceQuote?.Roof?.PricingValue || null,
                    MonthlySunnovaPayment: priceQuote?.Roof?.MonthlySunnovaPayment || null,
                    FinanceType: priceQuote?.Roof?.FinanceType || null,
                    FicoMin: priceQuote?.Roof?.FicoMin || null
                },
                RoofAdders: roofDetails?.RoofAdders || [],
                BatteryAdders: [],
                OpportunityInterest: OpportunityInterest
            };

            if (confirmQuoteCellKey?.product.includes("Solar") && (productType == 'S' || productType == 'SR' || productType == 'SB' || productType == 'SBR')) {
                let solar: SolarObject = {
                    IsDomestic: solarDetails?.SyncProductInfo?.IsDomestic,
                    SolarPackage: true,
                    FinancePartnerId: Number(solarDetails?.financePartnerId),
                    Total_Amount: (solarDetails?.Total_Amount) ? solarDetails?.Total_Amount : 0,
                    Deposit_Amount: (solarDetails?.Deposit_Amount) ? solarDetails?.Deposit_Amount : null,
                    ContractType: solarDetails?.contract_Type.trim() || '',
                    Tax: Number(solarDetails?.tax),
                    DownPayment: Number(solarDetails?.downPayment),
                    EPC: solarDetails?.epc || 0,
                    Escalator: (solarDetails?.Escalator) ? String(solarDetails?.Escalator) : '0',
                    PPW: solarDetails?.ppw || 0,
                    Rate: solarDetails?.SolarProductionRate ? Number(solarDetails.SolarProductionRate) : null,
                    Rebate: Number(solarDetails?.rebate),
                    TermYears: Number((sunnovaAPIDetails) ? sunnovaAPIDetails.TermLength : 0),
                    PricingMethod: (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                    PricingValue: (solarDetails?.financePartnerName?.toLocaleLowerCase() != 'cash')?Number(solarDetails?.pricingValue):0,
                    ITC_Amount: solarDetails?.ITC_Amount,
                    Monthly_Finance_Payment_NoITCPayback: solarDetails?.Monthly_Finance_Payment_NoITCPayback,
                    Rebate_Method: solarDetails?.rebateMethod || '',
                    Rebate_Amount_PerWatt: solarDetails?.RebateAmountPerWatt || 0,
                    Adder_Price: solarDetails?.AdderCost || 0,
                    Desired_PPW_Net: Number(solarDetails?.DesiredPPWNet),
                    Finance_Partner_Product_Id: solarDetails?.Finance_Partner_Product_Id || '',
                    IsACH: (solarDetails?.IsACH && solarDetails.IsACH.toLowerCase() === 'true') ? true : false,
                    Product_Type: solarDetails?.Product_Type || '',
                    Financed_Amount: (solarDetails?.Financed_Amount) ? solarDetails?.Financed_Amount : null,
                    AmountOnContractCompletion: (solarDetails?.UponCompletionOfTheContract) ? solarDetails?.UponCompletionOfTheContract : 0,
                    AmountOnPhysicalInstallCompletion: (solarDetails?.UponCompletionOfPhysicalInstall) ? solarDetails?.UponCompletionOfPhysicalInstall : 0,
                    AmountOnPhysicalInstallStart: (solarDetails?.UponCommencementOfPhysicalInstall) ? solarDetails?.UponCommencementOfPhysicalInstall : 0,
                    AmountOnWorkCompletion: undefined,
                    Finance_partner_contract_Type: sunnovaAPIDetails ? sunnovaAPIDetails.Name : '',
                    Offer_Id: solarDetails?.Offer_Id ?? ''
                }

                payload.Solar = solar
            }

            /**Set initial roof data  from retive api response */
            if (confirmQuoteCellKey?.product.includes("Roof") && (OpportunityInterest.Roof || OpportunityInterest.RoofOnly)) {

                const roofQuoteObj = {
                    RoofPackage: false,
                    FinancePartnerId: priceQuote.RoofQuote?.FinancePartnerId,
                    ContractType: priceQuote.RoofQuote?.ContractType,
                    Finance_Partner_Product_Id: priceQuote.RoofQuote?.Finance_Partner_Product_Id || null,
                    Offer_Id: priceQuote.RoofQuote?.Offer_Id || '',
                    TermYears: priceQuote.RoofQuote?.TermYears || 0,
                    TermAPR: priceQuote.RoofQuote?.TermAPR || 0,
                    DownPayment: priceQuote.RoofQuote?.DownPayment || 0,
                    Normal_Slope_Price_Override: priceQuote.RoofQuote?.Normal_Slope_Price_Override || null,
                    Low_Slope_Price_Override: priceQuote.RoofQuote?.Low_Slope_Price_Override || null,
                    Pricing_Override_Type: priceQuote.RoofQuote?.Pricing_Override_Type || null,
                    Total_Roofing_Override: priceQuote.RoofQuote?.Total_Roofing_Override || null,
                    Deposit_Amount: priceQuote.RoofQuote?.Deposit_Amount || null,
                    Financed_Amount: priceQuote.RoofQuote?.Financed_Amount || null,
                    AmountOnWorkCompletion: priceQuote.RoofQuote?.AmountOnWorkCompletion || 0,
                    DesignId: priceQuote.RoofQuote?.DesignId || 0
                };
                payload.RoofQuote = roofQuoteObj;

                // Solar + Roof (SR) or Battery + Roof (BR): roof line from modal; multi-contract when roof contract ≠ primary line (solar or battery).
                if ((productType === 'SR' || productType === 'BR') && OpportunityInterest?.Roof) {
                    payload.RoofQuote.RoofPackage = true;

                    const primaryContractType = productType === 'SR'
                        ? String((payload.Solar as SolarObject)?.ContractType || '').trim()
                        : String(batteryDetails?.financeMethodName || '').trim();
                    const roofContractType = roofDetails?.contract_Type?.trim() || '';

                    if (roofContractType !== '' && primaryContractType !== '' && roofContractType !== primaryContractType) {
                        quoteIsMultiContract = true;
                    }

                    if (!quoteIsMultiContract) {
                        payload.RoofQuote.FinancePartnerId = roofDetails?.financePartnerId;
                        payload.RoofQuote.ContractType = roofContractType;
                        payload.RoofQuote.TermYears = String(roofDetails?.term || null);
                        payload.RoofQuote.TermAPR = String(roofDetails?.apr || '');
                        payload.RoofQuote.DownPayment = roofDetails?.CustomerDeposit;
                        payload.TotalSaleAmount = Number(roofDetails?.gross_Sales_Price || 0);
                    } else {
                        payload.RoofOnlyPackage.FinancePartnerId = roofDetails?.financePartnerId;
                        payload.RoofOnlyPackage.ContractType = roofContractType;
                        payload.RoofOnlyPackage.TermYears = String(roofDetails?.term || null);
                        payload.RoofOnlyPackage.TermAPR = String(roofDetails?.apr || '');
                        payload.RoofOnlyPackage.DownPayment = roofDetails?.CustomerDeposit;
                    }
                    payload.RoofAdders = roofDetails?.RoofAdders || [];
                    payload.TotalCommissionableAmount = Number(roofDetails?.sales_Commission);
                    //  payload.TotalRoof = roofDetails?.Roofsq;
                    payload.RoofCostType = roofDetails?.RoofCostType;
                    payload.RoofCommissionTier = roofDetails?.commission_Tier;
                    payload.RoofFinancingPartner = roofDetails?.financePartnerName;
                    payload.RoofFinanceMethod = roofDetails?.contract_Type.trim()
                    payload.RoofFinanceTerm = (roofDetails?.financePartnerName != 'Cash') ? String(roofDetails?.term) : roofDetails?.contract_Type.trim();
                    payload.PurchaseMethod = roofDetails?.purchaseMethod;
                    payload.RoofQuote.Normal_Slope_Price_Override = (roofDetails?.roofForm.roofCostDetails.Normal_Slope_Price_Override != '' ? roofDetails?.roofForm.roofCostDetails.Normal_Slope_Price_Override : null);
                    payload.RoofQuote.Low_Slope_Price_Override = (roofDetails?.roofForm.roofCostDetails.Low_Slope_Price_Override != '' ? roofDetails?.roofForm.roofCostDetails.Low_Slope_Price_Override : null);
                    payload.RoofQuote.Pricing_Override_Type = (roofDetails?.roofForm.roofCostDetails.Pricing_Override_Type != '' ? roofDetails?.roofForm.roofCostDetails.Pricing_Override_Type : null);
                    payload.RoofQuote.Total_Roofing_Override = (roofDetails?.roofForm.roofCostDetails.Total_Roofing_Override != '' ? roofDetails?.roofForm.roofCostDetails.Total_Roofing_Override : null);
                    payload.RoofQuote.Offer_Id = roofDetails?.Offer_Id;
                    payload.RoofQuote.Finance_Partner_Product_Id = roofDetails?.Finance_Partner_Product_Id;
                    payload.RoofQuote.Deposit_Amount = (roofDetails?.Deposit_Amount) ? roofDetails?.Deposit_Amount : null;
                    payload.RoofQuote.Financed_Amount = (roofDetails?.Financed_Amount) ? roofDetails?.Financed_Amount : null;
                }

                let qgId = effectiveQuoteGroupIdForSrPack(productType, OpportunityInterest, quoteGroupId);
                if (productType == 'SR' && OpportunityInterest.RRR) productType = 'SRRR';
                else if (productType == 'R' && OpportunityInterest.RRR) productType = 'RRR';
                else if (productType == 'BR' && OpportunityInterest.RRR) productType = 'BRRR';
                else if (productType == 'SBR' && OpportunityInterest.RRR) productType = 'SBRRR';
                else if (productType == 'RR' && OpportunityInterest.RR) productType = 'RR';

                const roofOnlyObj = {
                    RoofOnlyPackage: true,
                    ContractType: priceQuote.RoofOnly?.ContractType || '',
                    Tax: priceQuote.RoofOnly?.Tax || null,
                    DownPayment: priceQuote.RoofOnly?.DownPayment || null,
                    EPC: priceQuote.RoofOnly?.EPC || null,
                    Escalator: priceQuote.RoofOnly?.Escalator || null,
                    PPW: priceQuote.RoofOnly?.PPW || null,
                    Rate: priceQuote.RoofOnly?.Rate || null,
                    Rebate: priceQuote.RoofOnly?.Rebate || null,
                    TermYears: priceQuote.RoofOnly?.TermYears || 0,
                    PricingMethod: priceQuote.RoofOnly?.PricingMethod || '',
                    PricingValue: priceQuote.RoofOnly?.PricingValue || null,
                    PermitPayer: priceQuote.RoofOnly?.PermitPayer || '',
                    TermAPR: priceQuote.RoofOnly?.TermAPR || 0,
                    Offer_Id: priceQuote.RoofOnly?.Offer_Id || '',
                    Finance_Partner_Product_Id: priceQuote.RoofOnly?.Finance_Partner_Product_Id || null,
                    DesignId: priceQuote.RoofOnly?.DesignId || 0
                };
                payload.RoofOnlyPackage = roofOnlyObj;

            }

            /**set payload from roof details */
            if ((OpportunityInterest.RoofOnly || OpportunityInterest.Roof) && productType == 'R') {
                payload.RoofOnlyPackage.FinancePartnerId = roofDetails?.financePartnerId;
                payload.RoofOnlyPackage.ContractType = roofDetails?.contract_Type?.trim();
                payload.RoofOnlyPackage.TermYears = String(roofDetails?.term || 0);
                payload.RoofOnlyPackage.TermAPR = String(roofDetails?.apr || 0);
                payload.RoofOnlyPackage.DownPayment = roofDetails?.CustomerDeposit;
                payload.RoofOnlyPackage.Offer_Id = roofDetails?.Offer_Id;
                payload.RoofAdders = roofDetails?.RoofAdders || [];
                payload.TotalCommissionableAmount = Number(roofDetails?.sales_Commission || 0);
                payload.TotalSaleAmount = Number(roofDetails?.gross_Sales_Price || 0);
                payload.RoofCostType = roofDetails?.RoofCostType;
                payload.RoofCommissionTier = roofDetails?.commission_Tier;
                payload.RoofFinancingPartner = roofDetails?.financePartnerName;
                payload.RoofFinanceMethod = roofDetails?.contract_Type?.trim();
                payload.RoofFinanceTerm = (roofDetails?.financePartnerName !== 'Cash') ? String(roofDetails?.term || '') : roofDetails?.contract_Type?.trim();
                payload.PurchaseMethod = roofDetails?.purchaseMethod;
                payload.SfdcSyncValue = sunnovaAPIDetails?.SfdcSyncValue || '';
                payload.RoofQuote.Normal_Slope_Price_Override = roofDetails?.normalSlopePriceOverride || null;
                payload.RoofQuote.Low_Slope_Price_Override = roofDetails?.lowSlopePriceOverride || null;
                payload.RoofQuote.Pricing_Override_Type = roofDetails?.Pricing_Override_Type || null;
                payload.RoofQuote.Total_Roofing_Override = roofDetails?.Total_Roofing_Override || null;
                payload.RoofQuote.Offer_Id = roofDetails?.Offer_Id;
                payload.RoofQuote.Finance_Partner_Product_Id = roofDetails?.financePartnerProductId || null;
                payload.RoofQuote.Deposit_Amount = roofDetails?.Deposit_Amount || null;
                payload.RoofQuote.Financed_Amount = roofDetails?.Financed_Amount || null;
                payload.RoofQuote.AmountOnWorkCompletion = roofDetails?.PlusAddersUponCompletionOfWork || 0;
                payload.Dealer_Fee_Percentage = roofDetails?.Dealer_Fee_Percentage || 0;
                payload.Dealer_Epc = roofDetails?.Dealer_Epc || 0;
            }

            if (confirmQuoteCellKey?.product.includes("Battery") && (productType == 'B' || productType === 'BR')) {
                // Standalone B: headline totals from battery. Combined BR: roof block above sets headline totals; only attach battery package + adders.
                if (productType === 'B') {
                    payload.PriceTier = batteryDetails?.priceTier || null;
                    payload.TotalSaleAmount = (batteryDetails?.grossSalesPrice || 0);
                    payload.TotalCommissionableAmount = batteryDetails?.totalCommissionableAmount || 0;
                    payload.PurchaseMethod = (batteryDetails?.purchaseMethod || '');
                    payload.RoofCommissionTier = roofDetails?.commission_Tier;
                    payload.Dealer_Fee_Percentage = batteryDetails?.dealerFee || 0;
                    payload.Dealer_Epc =  solarDetails?.dealerEpc ?? (priceQuote.Dealer_Epc || 0);
                    payload.Monthly_Finance_Partner_Payment = batteryDetails?.monthlyPayment || 0;
                }

                payload.BatteryOnlyPackage = {
                    BatteryOnlyPackage: true,
                    FinancePartnerId: Number(batteryDetails?.financePartnerId || 0),
                    ContractType: (batteryDetails?.financeMethodName || '').trim(),
                    TermYears: String(batteryDetails?.term) || null,
                    TermAPR: String(batteryDetails?.apr) || null,
                    DownPayment: Number(batteryDetails?.downPayment || 0),
                    Rebate: Number(batteryDetails?.rebate || 0),
                    Tax: Number(batteryDetails?.tax || 0),
                    PricingMethod: (batteryDetails?.pricingMethod || '').trim(),
                    PricingValue: Number(batteryDetails?.price || 0),
                    Rate: Number(batteryDetails?.rate || 0),
                    Offer_Id: batteryDetails?.Offer_Id || '',
                    Finance_Partner_Product_Id: batteryDetails?.Finance_Partner_Product_Id || '',
                    Deposit_Amount: batteryDetails?.depositAmount || null,
                    Financed_Amount: batteryDetails?.financedAmount || 0,
                    AmountOnPhysicalInstallCompletion: batteryDetails?.uponCompletionOfPhysicalInstall || 0,
                    AmountOnWorkCompletion: batteryDetails?.uponCompletionOfWork || 0,
                    Total_Amount: batteryDetails?.grossSalesPrice || 0,
                }

                payload.BatteryAdders = batteryDetails?.selectedAdders ? batteryDetails.selectedAdders.map((a: string) => ({
                    AdderSelect: a,
                    amount: batteryDetails.additionalCost,
                    pricing_Type: batteryDetails.priceTier,
                    Adder_Cost: batteryDetails.totalAdderCost
                })) : [];
            }
            if (confirmQuoteCellKey?.product.includes("Battery") && (productType == 'SB')){
                payload.Battery = {
                    BatteryPackage: true,
                    FinancePartnerId: Number(batteryDetails?.financePartnerId || 0),
                    ContractType: (batteryDetails?.financeMethodName || '').trim(),
                    TermYears: String(batteryDetails?.term) || null,
                    TermAPR: String(batteryDetails?.apr) || null,
                    DownPayment: Number(batteryDetails?.downPayment || 0),
                    Rebate: Number(batteryDetails?.rebate || 0),
                    Tax: Number(batteryDetails?.tax || 0),
                    PricingMethod: (batteryDetails?.pricingMethod || '').trim(),
                    PricingValue: Number(batteryDetails?.price || 0),
                    Rate: Number(batteryDetails?.rate || 0),
                    Offer_Id: batteryDetails?.Offer_Id || '',
                    Finance_Partner_Product_Id: batteryDetails?.Finance_Partner_Product_Id || '',
                    Deposit_Amount: batteryDetails?.depositAmount || null,
                    Financed_Amount: batteryDetails?.financedAmount || 0,
                    AmountOnPhysicalInstallCompletion: batteryDetails?.uponCompletionOfPhysicalInstall || 0,
                    AmountOnWorkCompletion: batteryDetails?.uponCompletionOfWork || 0,
                    Total_Amount: batteryDetails?.grossSalesPrice || 0,
                }
                if(batteryDetails?.grossSalesPrice) payload.TotalSaleAmount = (batteryDetails?.grossSalesPrice);
                payload.TotalCommissionableAmount = Number(batteryDetails?.totalCommissionableAmount);
                payload.BatteryAdders = batteryDetails?.selectedAdders ? batteryDetails.selectedAdders.map((a: string) => ({
                    AdderSelect: a,
                    amount: batteryDetails.additionalCost,
                    pricing_Type: batteryDetails.priceTier,
                    Adder_Cost: batteryDetails.totalAdderCost
                })) : [];
            }

            let qgId = effectiveQuoteGroupIdForSrPack(productType, OpportunityInterest, quoteGroupId);
            if (productType == 'SR' && OpportunityInterest.RRR) productType = 'SRRR';
            else if (productType == 'R' && OpportunityInterest.RRR) productType = 'RRR';
            else if (productType == 'BR' && OpportunityInterest.RRR) productType = 'BRRR';
            else if (productType == 'SBR' && OpportunityInterest.RRR) productType = 'SBRRR';
            else if (productType == 'RR' && OpportunityInterest.RR) productType = 'RR';

            if(confirmQuoteCellKey?.clonedQuoteDetails) {
                payload.Cloned_Quote_Id = confirmQuoteCellKey.clonedQuoteDetails.QuoteId;
            }

            savePriceAndQuote(payload, productType).then((response: any) => {
                // setFooterMessage({
                //     type: 'success',
                //     text: `${getProductName(productType)} quote created successfully`,
                // });

                noticeApi.success({
                    message: 'Quote Created',
                    description: `${getProductName(productType)} quote created successfully`,
                    className: notifClass,
                });
                resolve(response);
            }).catch((err) => {
                const msg = err?.response?.data?.Message
                    ?? err?.error?.Message
                    ?? err?.Message
                    ?? `Failed to generate ${getProductName(productType)} quote. Please try again.`;
                setFooterMessage({
                    type: 'error',
                    text: typeof msg === 'string' ? msg : String(msg),
                });
                reject(err);
            });
        });

        // }
    }

    // Step 4
    async function savePriceAndQuote(obj: any, productTypeName: string) {
        // For single contract, quoteGroupId will be null.
        updateLoaderCount(true);
        try {
            const res: any = await quoteServices.savePriceAndQuote(obj, productTypeName);
            const data = {
                quoteId: Number(res.Message),
                opportunity_Id: priceQuote.opportunity_Id,
                salesforceID: priceQuote.SalesForce_Id
            };

            // Angular: `SolarProductName` || `SolarProductNotation` (notation is `S`; combined codes like `SB` skip priceCheck).
            if (
                productTypeName === COMMON_CONSTANTS.SolarProductName
                || productTypeName === 'S'
            ) {
                try {
                    await quoteServices.priceCheck(data);
                } catch (_) {
                    /* Angular resolves promise even when priceCheck errors */
                }
            }

            const financePartnerName = checkProductTypeFinancePartner(obj, productTypeName, opportunityDetails);
            const oppId = priceQuote.Opportunity_Id;

            if (financePartnerName === COMMON_CONSTANTS.GoodLeapPartnerName) {
                try {
                    await quoteServices.contractUpdateLoanStatus(oppId, 'Quote', Number(res.Message));
                    await quoteServices.contractGetLoanStatusByOpportunity(oppId);
                } catch (_) {
                    /* Angular: nested subscribe — no user toast on failure */
                }
            }

            const hasLightReachfinancePartner =
                financePartnerName === COMMON_CONSTANTS.LightreachPartnerName;
            if (hasLightReachfinancePartner) {
                try {
                    const changeOrderDetails = await quoteServices.contractGetLoanStatusByOpportunity(oppId);
                    const contractStatus = Array.isArray(changeOrderDetails)
                        ? changeOrderDetails.find(
                            (x: any) =>
                                x.Finance_Partner_Name === COMMON_CONSTANTS.LightreachPartnerName
                        )
                        : undefined;
                    const appStatus = contractStatus?.application_Status;
                    if (
                        appStatus === 'Signed'
                        || appStatus === 'Sent'
                        || appStatus === 'Generated'
                    ) {
                        await quoteServices.contractUpdateLoanStatus(oppId, 'Quote', 0);
                        await quoteServices.contractGetLoanStatusByOpportunity(oppId);
                        const financeMethod =
                            obj?.Solar?.Finance_Method_Name ?? obj?.Solar?.ContractType ?? '';
                        let changeOrderMessage =
                            `Quote created: Finance method - ${financeMethod},Escalator - ${obj?.Solar?.Escalator}%,Pricing - ${obj?.Solar?.PricingMethod},Solar rate ${obj?.Solar?.PricingValue}`;
                        const battQty =
                            obj?.BatteryInformation?.Quantity ?? priceQuote?.BatteryInformation?.Quantity;
                        if (battQty) {
                            changeOrderMessage += `,Battery count - ${battQty}.`;
                        }
                        await quoteServices.savePageChanges({
                            opportunityId: oppId,
                            pageName: 'Quote',
                            changes: changeOrderMessage,
                        });
                        await quoteServices.getPageChanges(oppId);
                    }
                } catch (_) {
                    /* Angular: no toast from this branch */
                }
            }

            return res;
        } catch (err) {
            console.error('Error in savePriceAndQuote:', err);
            throw err;
        } finally {
            updateLoaderCount(false);
        }
    }

    // Step 5

    const postSavePriceAndQuoteOperations = () => {
        if (loaderCount > 0) return;
        updateQuoteVersion();
    };

    const getProductName = (productType: string) => {
        let productName = '';
        switch (productType) {
            case "S":
                productName = "Solar";
                break;
            case "B":
                productName = "Battery";
                break;
            case "R":
                productName = "Roof";
                break;
            case "SR":
                productName = "Solar + Roof";
                break;
            case "SB":
                productName = "Solar + Battery";
                break;
            case "BR":
                productName = "Battery + Roof";
                break;
            case "SBR":
                productName = "Solar + Battery + Roof";
                break;
        }

        return productName;
    }


    const updateLoaderCount = React.useCallback((loader: boolean) => {

        setLoaderCount((prev) => {
            let loaders = prev < 0 ? 0 : prev;
            if (loader) {
                loaders++;
            } else {
                loaders = loaders > 0 ? loaders - 1 : 0;
            }
            return loaders;
        });
    }, []);

    useEffect(() => {
        let disposed = false;
        const hasSolarTab = tabs.some((tab) => tab.kind === 'solar');
        const syncLightreachData = async () => {
            if (!isLightReach || !hasSolarTab) {
                setLightreachEligibleProductData([]);
                setLightreachPriceCapsData([]);
                return;
            }

            const opportunityId = priceQuote?.Opportunity_Id;
            const leadId =
                opportunityDetails?.OpportunityDetails?.Finance_Partner_Sync_LeadID
                ?? priceQuote?.Finance_Partner_Sync_LeadID;

            if (!opportunityId || !leadId) {
                setLightreachEligibleProductData([]);
                setLightreachPriceCapsData([]);
                return;
            }

            updateLoaderCount(true);
            try {
                const [eligibleData, priceCaps] = await Promise.all([
                    quoteServices.getLightreachPricingByAccount(opportunityId, leadId),
                    quoteServices.getPriceCaps(leadId),
                ]);
                if (!disposed) {
                    setLightreachEligibleProductData(Array.isArray(eligibleData) ? eligibleData : []);
                    setLightreachPriceCapsData(Array.isArray(priceCaps) ? priceCaps : []);
                }
            } catch (error) {
                if (!disposed) {
                    setLightreachEligibleProductData([]);
                    setLightreachPriceCapsData([]);
                }
            } finally {
                if (!disposed) updateLoaderCount(false);
            }
        };

        syncLightreachData();

        return () => {
            disposed = true;
        };
    }, []);

    const setErrorMessage = (message: string) => {
        if (message?.trim()) {
            setFooterMessage({ type: 'error', text: message });
            return;
        }
        setFooterMessage(null);
    }

    async function GetOneDrawDesignReadiness() {
        return new Promise<any>((resolve, reject) => {
            updateLoaderCount(true);
            quoteServices.GetOneDrawDesignReadiness(priceQuote.Opportunity_Id).then((res: any) => {
                updateLoaderCount(false);
                resolve(res);
            }, err => {
                updateLoaderCount(false);
                resolve(false);
            })
        })

    }
    const showMultiHint =
        confirmQuoteCellKey &&
        (columnIncludesSolar(confirmQuoteCellKey.product) ? 1 : 0) +
        (columnIncludesRoof(confirmQuoteCellKey.product) ? 1 : 0) +
        (columnIncludesBattery(confirmQuoteCellKey.product) ? 1 : 0) >
        1;

    const getConfirmQuoteCellKey = (prduct: string) => {
        let tab:any = tabs.find((x: any) => x.key === prduct);
        tab.quote.clonedQuoteDetails = confirmQuoteCellKey?.clonedQuoteDetails
        return tab?.quote 
    }


    const getTabContent = (tab: { key: string; kind: string; label: string; quote: selectedQuote }) => {
        switch (tab.kind) {
            case 'solar':
                return (
                    <SolarQuoteDetails
                        confirmQuoteCellKey={getConfirmQuoteCellKey('solar') as selectedQuote}
                        priceQuote={priceQuote}
                        theme={theme}
                        lineMode="solar"
                        lightreachEligibleProductData={lightreachEligibleProductData}
                        lightreachPriceCapsData={lightreachPriceCapsData}
                        updateLoaderCount={updateLoaderCount}
                        updateSolarDetails={(data: any) => { setSolarDetails(data) }}
                        updateErrorMessage={(message: string) => { setErrorMessage(message) }}
                    />
                );
            case 'battery':
                return (
                    <BatteryQuoteDetails
                        confirmQuoteCellKey={tab.quote as selectedQuote}
                        priceQuote={priceQuote}
                        theme={theme}
                        updateLoaderCount={updateLoaderCount}
                        lightreachPriceCapsData={lightreachPriceCapsData}
                        updateErrorMessage={(message: string) => { setErrorMessage(message) }}
                        updateBatteryDetails={(data: any) => { setBatteryDetails(data) }}
                        solarMasterSync={batterySolarMasterSync}
                    />
                );
            case 'roof':
                return (
                    <RoofQuoteDetails
                        confirmQuoteCellKey={tab.quote as selectedQuote}
                        priceQuote={priceQuote}
                        theme={theme}
                        updateLoaderCount={updateLoaderCount}
                        solarMasterSync={roofSolarMasterSync}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <DashboardModal
                open={!!confirmQuoteCellKey}
                onClose={handleCancel}
                title="Generate Quote"
                width="min(1280px, calc(100vw - 48px))"
                zIndex={10080}
                theme={theme}
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-theme-${theme} obd-generate-quote-modal-root obd-generate-quote-modal-root--wide`}
                className={`obd-pricing-confirm-modal obd-theme-${theme}`}
                loading={loaderCount > 0}
                loadingMessage={isGenerating ? 'Generating Quote…' : 'Loading…'}
                footer={
                    <div className="obd-generate-quote-footer">
                        {footerMessage && (
                            <Alert
                                className="obd-generate-quote-footer__message"
                                message={footerMessage.text}
                                type={footerMessage.type}
                                showIcon
                            />
                        )}
                        <div className="obd-generate-quote-footer__actions">
                            <Button onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleOk}
                                loading={loaderCount > 0 && isGenerating}
                                disabled={loaderCount > 0 || footerMessage?.type === 'error' || !(systemDesign?.DesignArray != null && systemDesign?.DesignArray.length > 0 && (systemDesign?.DesignArray as any[])[0].ArrayId) }
                            >
                                Generate
                            </Button>
                        </div>
                    </div>
                }
            >
                <div className="obd-generate-quote-modal-inner" style={{ position: 'relative' }}>
                    {confirmQuoteCellKey && (
                        <>
                            {tabs.length > 0 && (
                                <div className="obd-generate-quote-stack" role="region" aria-label="Generate quote by product">
                                    {tabs.map((tab) => (
                                        <div
                                            key={`${tab.key}-${tab.quote.partner}-${tab.quote.product}`}
                                            className="obd-generate-quote-stack__block"
                                        >
                                            {getTabContent(tab)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </DashboardModal>
            <Modal
                title="Sync to Lightreach required"
                open={lightreachSyncRequiredOpen}
                zIndex={13200}
                centered
                maskClosable
                rootClassName={`obd-theme-${theme} obd-generate-quote-lightreach-sync`}
                onCancel={() => setLightreachSyncRequiredOpen(false)}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            onClick={() => setLightreachSyncRequiredOpen(false)}
                        >
                            OK
                        </Button>
                    </div>
                }
            >
                <p className="obd-generate-quote-lightreach-sync__body" style={{ margin: 0 }}>
                    {LIGHTREACH_SYNC_REQUIRED_MESSAGE}
                </p>
            </Modal>
        </>
    );
};

export default GenerateQuoteModal;
